import { LegacyRef, forwardRef, type IframeHTMLAttributes } from 'react'
import ReactMarkdown, { Options } from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkMath from 'remark-math'
import rehypeHighlight from 'rehype-highlight'
import rehypeRaw from 'rehype-raw'
import remarkSupersub from 'remark-supersub'
import remarkGfm from 'remark-gfm'
import { withPrefix } from 'gatsby'
import LinkComponent from '../LinkComponent/LinkComponent'
import {
  referrerPolicyForMarkdownYoutubeIframe,
  sendCustomGAEvent,
} from 'shared-utilities'
import 'katex/dist/katex.min.css'

interface IMarkdownTextProps {
  text: string
  className?: string
  testId?: string
  gaEventCategory?: string
  gaEventLabel?: string
  gaEventValue?: string
  style?: React.CSSProperties
}

const MarkdownText = forwardRef(function MarkdownText(
  {
    text,
    className = '',
    testId = 'markdown-text',
    gaEventCategory,
    gaEventLabel,
    gaEventValue,
    style,
  }: IMarkdownTextProps,
  ref: LegacyRef<HTMLDivElement> | undefined
) {
  return (
    <div className={`${className}`} data-testid={testId} ref={ref}>
      <ReactMarkdown
        remarkPlugins={
          [remarkMath, remarkGfm, remarkSupersub] as Options['remarkPlugins']
        }
        rehypePlugins={
          [rehypeKatex, rehypeRaw, rehypeHighlight] as Options['rehypePlugins']
        }
        linkTarget="_blank"
        components={{
          h1: 'h2',
          img: ({ ...props }) => (
            // eslint-disable-next-line react/prop-types
            <img {...props} src={withPrefix(String(props.src))} />
          ),
          p: ({ children }) => (
            <p style={{ ...style }} className="leading-normal">
              {children}
            </p>
          ),
          a: ({
            children,
            ...props
          }: {
            children?: React.ReactNode
            href?: string
          }) => (
            <LinkComponent
              {...props}
              // eslint-disable-next-line react/prop-types
              href={String(props.href)}
              onClick={
                gaEventCategory
                  ? () => {
                      sendCustomGAEvent('click', {
                        event_category: gaEventCategory,
                        event_label:
                          gaEventLabel || String(children?.[0] ?? ''),
                        value: gaEventValue || props.href || '',
                      })
                    }
                  : undefined
              }
            >
              {children}
            </LinkComponent>
          ),
          iframe: ({
            src,
            referrerPolicy,
            ...props
          }: IframeHTMLAttributes<HTMLIFrameElement>) => (
            <iframe
              {...props}
              src={src}
              referrerPolicy={referrerPolicyForMarkdownYoutubeIframe(
                typeof src === 'string' ? src : undefined,
                referrerPolicy
              )}
            />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  )
})

export default MarkdownText
